import { MonthPicker, Tooltip, type CivilDate } from '@hue/design-system';
import { CalendarRange, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  OperationalPeriodContext,
  monthBounds,
  useOperationalPeriod,
  type OperationalMonth,
} from './operational-period-context.js';
const VALID_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/u;

export function OperationalPeriodProvider({
  companyKey,
  today,
  children,
}: {
  companyKey: string;
  today: CivilDate;
  children: ReactNode;
}) {
  const storageKey = `hue.operational-month.${companyKey}`;
  const currentMonth = today.slice(0, 7) as OperationalMonth;
  const [month, setMonthState] = useState<OperationalMonth>(() => {
    const saved = globalThis.sessionStorage?.getItem(storageKey);
    return saved === 'ALL' || (saved && VALID_MONTH.test(saved))
      ? (saved as OperationalMonth)
      : currentMonth;
  });
  const setMonth = useCallback(
    (next: OperationalMonth) => {
      setMonthState(next);
      globalThis.sessionStorage?.setItem(storageKey, next);
    },
    [storageKey],
  );
  const value = useMemo(() => ({ month, setMonth, ...monthBounds(month) }), [month, setMonth]);
  return (
    <OperationalPeriodContext.Provider value={value}>{children}</OperationalPeriodContext.Provider>
  );
}

export function OperationalPeriodBar({ today }: { today: CivilDate }) {
  const { month, setMonth } = useOperationalPeriod();
  const currentMonth = today.slice(0, 7) as OperationalMonth;
  const center = month === 'ALL' ? currentMonth : month;
  return (
    <div className="border-b border-surface-border bg-surface-card/70">
      <div className="flex flex-wrap items-center gap-2 px-5 py-3 sm:px-8">
        <CalendarRange className="size-4 shrink-0 text-brand-400" aria-hidden="true" />
        <span className="mr-1 text-xs font-semibold text-content-muted">Mês de trabalho</span>
        <div className="inline-flex items-center gap-1 rounded-xl border border-surface-border bg-surface-card p-1 shadow-soft">
          <button
            type="button"
            aria-label="Mês anterior"
            disabled={month === 'ALL'}
            onClick={() => setMonth(shiftMonth(center, -1))}
            className="grid size-9 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronLeft className="size-4" />
          </button>
          <MonthPicker
            label="Mês de trabalho"
            value={month === 'ALL' ? null : month}
            onValueChange={(value) => setMonth((value ?? 'ALL') as OperationalMonth)}
          />
          <button
            type="button"
            aria-label="Próximo mês"
            disabled={month === 'ALL'}
            onClick={() => setMonth(shiftMonth(center, 1))}
            className="grid size-9 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => setMonth(currentMonth)}
          className="min-h-10 cursor-pointer rounded-xl px-3 text-xs font-semibold text-brand-400 transition hover:bg-brand-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          Mês atual
        </button>
        <Tooltip content="Este mês acompanha você entre as telas da empresa. Listas e indicadores que trabalham por período usam o mesmo recorte, evitando mudanças repetidas e lançamentos no mês errado.">
          <button
            type="button"
            aria-label="Como funciona o mês de trabalho"
            className="grid size-8 cursor-pointer place-items-center rounded-lg text-content-faint transition hover:bg-content/5 hover:text-content"
          >
            <Info className="size-4" />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

function shiftMonth(month: OperationalMonth, step: number): OperationalMonth {
  const [year, number] = month.split('-').map(Number) as [number, number];
  const total = year * 12 + number - 1 + step;
  return [String(Math.floor(total / 12)), String((total % 12) + 1).padStart(2, '0')].join(
    '-',
  ) as OperationalMonth;
}
